const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Append the OneDrive thumbnail `size` query param to a file-proxy URL.
 *
 * The backend route accepts ?size=preview|thumb|small|medium|large and
 * serves a pre-generated Microsoft thumbnail instead of the full-size
 * original. We use this for the field-report photo grids so we ship
 * ~1MP previews instead of 12MP iPhone originals into a 3.75" PDF cell.
 *
 * Only mutates file-proxy URLs — Cloudinary URLs (and bare strings the
 * caller already resolved) are returned untouched.
 */
function appendSize(url, size) {
  if (!url || !size) return url;
  if (!url.includes('/api/onedrive/file-proxy/')) return url;
  return url + (url.includes('?') ? '&' : '?') + `size=${encodeURIComponent(size)}`;
}

/**
 * Resolve a file URL — routes OneDrive files through the proxy endpoint.
 * Works with any file object that has storage, onedrive_id, public_id, or url fields.
 * Also handles plain URL strings.
 *
 * Optional `size` arg (e.g. 'preview') appends ?size= when the resolved
 * URL is a backend file-proxy URL — gets us the Microsoft thumbnail
 * instead of the full original. Safe no-op for Cloudinary / direct URLs.
 */
export function resolveFileUrl(fileOrUrl, size = null) {
  if (!fileOrUrl) return '';

  // Plain string URL
  if (typeof fileOrUrl === 'string') {
    // Check if it's already a proxy URL
    if (fileOrUrl.includes('/api/onedrive/file-proxy/')) return appendSize(fileOrUrl, size);
    // Cloudinary URLs are public, return as-is
    if (fileOrUrl.includes('cloudinary.com')) return fileOrUrl;
    return fileOrUrl;
  }

  // If URL already goes through proxy, use it
  if (fileOrUrl.url && fileOrUrl.url.includes('/api/onedrive/file-proxy/')) {
    return appendSize(fileOrUrl.url, size);
  }

  // OneDrive file — find the item ID and use proxy
  const onedriveId = fileOrUrl.onedrive_id || fileOrUrl.cloudinary_public_id || fileOrUrl.cloudinary_id || fileOrUrl.public_id;
  if (onedriveId && (fileOrUrl.storage === 'onedrive' || isOnedriveUrl(fileOrUrl.url))) {
    return appendSize(`${API}/api/onedrive/file-proxy/${onedriveId}`, size);
  }

  // Cloudinary URLs are public — return directly
  if (fileOrUrl.url && fileOrUrl.url.includes('cloudinary.com')) {
    return fileOrUrl.url;
  }

  // Default: use stored URL
  return fileOrUrl.url || '';
}

/**
 * Resolve a signature URL from a report/form object.
 * Usage: resolveSignatureUrl(report, 'submitter_signature')
 */
export function resolveSignatureUrl(obj, prefix) {
  if (!obj) return '';
  const url = obj[`${prefix}_url`];
  const storage = obj[`${prefix}_storage`];
  const id = obj[`${prefix}_cloudinary_id`];
  
  // Already proxy URL
  if (url && url.includes('/api/onedrive/file-proxy/')) return url;
  
  // OneDrive signature — use proxy
  if (id && (storage === 'onedrive' || isOnedriveUrl(url))) {
    return `${API}/api/onedrive/file-proxy/${id}`;
  }
  
  return url || '';
}

/**
 * Check if a URL is a OneDrive/SharePoint URL (not Cloudinary, not our proxy)
 */
function isOnedriveUrl(url) {
  if (!url) return false;
  return url.includes('1drv.ms') || 
         url.includes('sharepoint.com') || 
         url.includes('onedrive.live.com') ||
         url.includes('graph.microsoft.com');
}
